import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { getLocale } from 'umi';
import SvgIcon from '@/pages/home/components/SvgIcon';
import CommonBubble from '@home/components/CommonBubble';
import { IAllConfig, IImageTextSelectorProps, IOptionItem } from './types';
import './index.scss';

function OptionCardWrapper({
  className,
  onClick,
  children,
}: {
  className: string;
  onClick: () => void;
  children: (hovered: boolean) => React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={className}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children(hovered)}
    </div>
  );
}

const ImageTextSelector: React.FC<IImageTextSelectorProps> = (props) => {
  const {
    optionAll,
    optionsList,
    defaultValue,
    mode = 'multiple',
    onChange,
    className,
    style,
  } = props;

  const isEn = getLocale() === 'en-US';
  const isMultiple = mode === 'multiple';
  const hasAll = !!optionAll;

  // 计算初始值：优先 defaultValue > optionAll.value > optionsList 第一项
  const getInitialValue = (): string[] => {
    if (defaultValue?.length) return defaultValue;
    if (optionAll?.value) return [optionAll.value];
    if (optionsList?.[0]?.value) return [optionsList[0].value];
    return [];
  };

  // 内部状态
  const [value, setValue] = useState<string[]>(getInitialValue);

  /** 统一触发值变更 */
  const triggerChange = useCallback(
    (next: string[]) => {
      setValue(next);
      if (typeof onChange === 'function') {
        onChange(next);
      }
    },
    [onChange],
  );

  /** 获取显示名称 */
  const getDisplayName = useCallback(
    (item: IOptionItem | IAllConfig): string =>
      (isEn ? item?.nameEn : item?.nameCn) ?? '',
    [isEn],
  );

  /** 判断某个值是否选中 */
  const isSelected = useCallback(
    (val: string): boolean => !!value?.includes(val),
    [value],
  );

  /** 判断汇总是否选中 */
  const isSummaryActive = useMemo(
    () => hasAll && (isSelected(optionAll.value) || !value?.length),
    [hasAll, isSelected, optionAll?.value, value?.length],
  );

  /** 点击汇总 */
  const handleSummaryClick = useCallback((): void => {
    if (!optionAll || optionAll.disabled) return;
    triggerChange([optionAll.value]);
  }, [optionAll, triggerChange]);

  /** 多选取消全部后的兜底值 */
  const getFallbackValue = useCallback((): string[] => {
    // 有 optionAll 就回到 optionAll
    if (optionAll?.value) return [optionAll.value];
    // 没有 optionAll，回到第一个未禁用的选项
    const firstEnabled = optionsList?.find((item) => !item.disabled);
    return firstEnabled ? [firstEnabled.value] : [];
  }, [optionAll?.value, optionsList]);

  /** 点击选项 */
  const handleOptionClick = useCallback(
    (item: IOptionItem): void => {
      if (item?.disabled) return;

      // 单选模式直接选中
      if (!isMultiple) {
        triggerChange([item.value]);
        return;
      }

      // 多选模式：过滤掉 optionAll 的值
      const current = (value || []).filter((v) => v !== optionAll?.value);
      const next = isSelected(item.value)
        ? current.filter((v) => v !== item.value)
        : [...current, item.value];

      // 全部取消后自动兜底
      triggerChange(next.length ? next : getFallbackValue());
    },
    [isMultiple, triggerChange, optionAll?.value, value, isSelected, getFallbackValue],
  );

  /** 图片加载失败处理 */
  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>): void => {
      const img = e.target as HTMLImageElement;
      img.style.display = 'none';
      const fallback = img.nextElementSibling as HTMLElement;
      if (fallback) {
        fallback.style.display = 'flex';
      }
    },
    [],
  );

  /** 渲染卡片内容
   * ✅ 新增 item-inner 内层包裹，专门负责 overflow:hidden 裁图片圆角
   * 外层 item 保持 overflow:visible，让 ::after 蓝边可以往外扩
   */
  const renderCard = useCallback(
    (item: IOptionItem | IAllConfig, active: boolean, showCheckbox: boolean, tooltip?: string, hovered?: boolean) => {
      const imageDiv = (
        <div className="dbh-image-text-selector-image">
          {item?.image ? (
            <img src={item.image} alt={getDisplayName(item)} onError={handleImageError} />
          ) : null}
          <div
            className="dbh-image-text-selector-fallback"
            style={{ display: item?.image ? 'none' : 'flex' }}
          />
        </div>
      );

      return (
        <>
          <div className="dbh-image-text-selector-item-inner">
            {tooltip ? (
              <CommonBubble
                visible={hovered}
                trigger="empty"
                content={tooltip}
                placement="bottom"
                dark
                arrowPointAtCenter
              >
                {imageDiv}
              </CommonBubble>
            ) : imageDiv}

            <div
              className={classNames('dbh-image-text-selector-name', {
                'dbh-image-text-selector-name-active': active,
              })}
            >
              {getDisplayName(item)}
            </div>
          </div>

          {showCheckbox && active && (
            <div className="dbh-image-text-selector-checkbox dbh-image-text-selector-checkbox-checked">
              <SvgIcon type="right-good" />
            </div>
          )}
          {showCheckbox && !active && (
            <div className="dbh-image-text-selector-checkbox dbh-image-text-selector-checkbox-unchecked" />
          )}
        </>
      );
    },
    [getDisplayName, handleImageError],
  );

  /** 渲染选项列表 */
  const renderedOptions = useMemo(
    () =>
      optionsList?.map((item) => {
        const active = isSelected(item?.value);
        const itemDisabled = !!item?.disabled;
        const tooltip = isEn ? (item as IOptionItem).tooltipEn : (item as IOptionItem).tooltipCn;

        const itemClassName = classNames('dbh-image-text-selector-item', {
          'dbh-image-text-selector-item-active': active && !isMultiple,
          'dbh-image-text-selector-item-multiple': isMultiple,
          'dbh-image-text-selector-item-disabled': itemDisabled,
        });

        if (!tooltip) {
          return (
            <div key={item?.value} className={itemClassName} onClick={() => handleOptionClick(item)}>
              {renderCard(item, active, isMultiple)}
            </div>
          );
        }

        return (
          <OptionCardWrapper
            key={item?.value}
            className={itemClassName}
            onClick={() => handleOptionClick(item)}
          >
            {(hovered) => renderCard(item, active, isMultiple, tooltip, hovered)}
          </OptionCardWrapper>
        );
      }),
    [optionsList, isSelected, isMultiple, handleOptionClick, renderCard, isEn],
  );

  return (
    <div
      className={classNames('dbh-image-text-selector', className)}
      style={style}
    >
      {/* 汇总卡片：有配置才展示 */}
      {hasAll && (
        <div
          className={classNames(
            'dbh-image-text-selector-item',
            'dbh-image-text-selector-all',
            {
              'dbh-image-text-selector-item-active': isSummaryActive,
              'dbh-image-text-selector-item-disabled': !!optionAll.disabled,
            },
          )}
          onClick={handleSummaryClick}
        >
          {renderCard(optionAll, isSummaryActive, false)}
        </div>
      )}

      {/* 分隔线：有汇总且有选项时才展示 */}
      {hasAll && optionsList?.length > 0 && <div className="dbh-image-text-selector-divider" />}

      {/* 选项列表 */}
      <div className="dbh-image-text-selector-options">{renderedOptions}</div>
    </div>
  );
};

export default ImageTextSelector;